/**
 * OfficeEditor — a Word/Excel/PowerPoint document open in Collabora Online,
 * full screen over the app.
 *
 * Asks file-service for an edit session, then POSTs the WOPI token into the
 * editor iframe (Collabora's way: the token never lands in a URL or history).
 * Collabora talks back with postMessage: it announces Frame_Ready (we answer
 * Host_PostmessageReady), Document_Loaded, and UI_Close when its own close
 * button is used. Collabora saves when the last editor leaves, so closing is
 * just unmounting.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FileText, Loader2, X } from 'lucide-react';
import {
  openEditSession, editorUrl, EditSessionFailure,
  type EditSession, type EditSessionError,
} from '../../lib/officeEditor';

interface OfficeEditorProps {
  fileId: string;
  fileName: string;
  /** Read-only even for people who may edit */
  mode?: 'edit' | 'view';
  onClose: () => void;
}

interface CollaboraMessage {
  MessageId?: string;
  Values?: { Status?: string };
}

export function OfficeEditor({ fileId, fileName, mode = 'edit', onClose }: OfficeEditorProps) {
  const { t, i18n } = useTranslation();
  const [session, setSession] = useState<EditSession | null>(null);
  const [error, setError] = useState<EditSessionError | null>(null);
  const [loaded, setLoaded] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const frameName = `office-editor-${fileId}`;

  useEffect(() => {
    let cancelled = false;
    setSession(null);
    setError(null);
    setLoaded(false);
    openEditSession(fileId, mode)
      .then((s) => { if (!cancelled) setSession(s); })
      .catch((err: unknown) => { if (!cancelled) setError(err instanceof EditSessionFailure ? err.kind : 'generic'); });
    return () => { cancelled = true; };
  }, [fileId, mode]);

  // Once the session is here, post the token into the iframe
  useEffect(() => {
    if (session) formRef.current?.submit();
  }, [session]);

  useEffect(() => {
    if (!session) return;
    const editorOrigin = new URL(session.actionUrl).origin;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== editorOrigin || typeof event.data !== 'string') return;
      let msg: CollaboraMessage;
      try { msg = JSON.parse(event.data) as CollaboraMessage; } catch { return; }
      if (msg.MessageId === 'App_LoadingStatus') {
        if (msg.Values?.Status === 'Frame_Ready') {
          frameRef.current?.contentWindow?.postMessage(
            JSON.stringify({ MessageId: 'Host_PostmessageReady', SendTime: Date.now(), Values: {} }),
            editorOrigin,
          );
        }
        if (msg.Values?.Status === 'Document_Loaded') setLoaded(true);
      }
      if (msg.MessageId === 'UI_Close') onClose();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [session, onClose]);

  // Escape closes while focus is outside the editor (inside, Collabora keeps the key)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const readOnly = session !== null && !session.canEdit;

  // Portal to <body>: chat rows are positioned with a CSS transform, which would
  // make "fixed inset-0" cover only the row the attachment sits in.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('office.editorTitle', { name: fileName })}
      className="fixed inset-0 z-[60] flex flex-col bg-nord-0"
    >
      <div className="flex shrink-0 items-center gap-3 border-b border-nord-3/30 px-4 py-2">
        <FileText className="h-4 w-4 shrink-0 text-nord-8" aria-hidden="true" />
        <span className="min-w-0 truncate text-sm font-medium text-nord-6">{fileName}</span>
        {readOnly && (
          <span className="shrink-0 rounded-full bg-nord-2 px-2 py-0.5 text-2xs font-medium text-nord-4">
            {t('office.readOnly')}
          </span>
        )}
        <button
          onClick={onClose}
          className="ml-auto rounded p-1.5 text-nord-4 transition-colors hover:bg-nord-2 hover:text-nord-6"
          aria-label={t('office.close')}
          title={t('office.close')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="relative flex-1">
        {error ? (
          <div role="alert" className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-sm text-nord-4">{t(`office.errors.${error}`)}</p>
            <button onClick={onClose} className="btn-secondary text-sm">{t('office.close')}</button>
          </div>
        ) : (
          <>
            {session && (
              <iframe
                ref={frameRef}
                name={frameName}
                title={t('office.editorTitle', { name: fileName })}
                className="h-full w-full border-0"
                allow="clipboard-read; clipboard-write; fullscreen"
                // Fallback if Document_Loaded never arrives: don't hide the editor behind the spinner forever
                onLoad={() => { window.setTimeout(() => setLoaded(true), 8000); }}
              />
            )}
            {!loaded && (
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 bg-nord-0">
                <Loader2 className="h-6 w-6 animate-spin text-nord-muted" aria-hidden="true" />
                <span className="text-xs text-nord-muted">{t('office.loading')}</span>
              </div>
            )}
          </>
        )}
      </div>

      {session && (
        <form ref={formRef} method="post" action={editorUrl(session, i18n.language)} target={frameName} className="hidden">
          <input type="hidden" name="access_token" value={session.accessToken} />
          <input type="hidden" name="access_token_ttl" value={String(session.accessTokenTtl)} />
        </form>
      )}
    </div>,
    document.body,
  );
}
